import { useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";

interface DecisionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (text: string) => void;
  isPending?: boolean;
  title: string;
  description?: string;
  label: string;
  confirmLabel: string;
  /** Matn majburiymi (rad etish sababi) — bo'lsa, eng kam uzunlik. */
  minLength?: number;
  /** Tayyor javoblar — bosilsa maydonga yoziladi. */
  presets?: readonly string[];
  destructive?: boolean;
  /** Oynaning ustki qismidagi qo'shimcha ma'lumot (summa, karta). */
  children?: React.ReactNode;
}

/**
 * Admin qarori oynasi: izoh yoki sabab bilan tasdiqlash.
 *
 * Forma faqat oyna OCHIQ bo'lganda chiziladi — yopilganda yechiladi va
 * keyingi safar maydon bo'sh boshlanadi (tozalash effekti kerak emas).
 */
export const DecisionDialog = (props: DecisionDialogProps) => (
  <Dialog open={props.open} onOpenChange={props.onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{props.title}</DialogTitle>
        {props.description ? <DialogDescription>{props.description}</DialogDescription> : null}
      </DialogHeader>
      {props.open ? <DecisionForm {...props} /> : null}
    </DialogContent>
  </Dialog>
);

const DecisionForm = ({
  onOpenChange,
  onConfirm,
  isPending,
  label,
  confirmLabel,
  minLength = 0,
  presets,
  destructive,
  children,
}: DecisionDialogProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const tooShort = text.trim().length < minLength;

  return (
    <>
      {children}
      {presets && presets.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {presets.map(preset => (
            <Button
              key={preset}
              type="button"
              size="sm"
              variant={text === preset ? "default" : "outline"}
              className="h-auto rounded-full py-1 text-left whitespace-normal"
              onClick={() => setText(preset)}
            >
              {preset}
            </Button>
          ))}
        </div>
      ) : null}
      <Field>
        <FieldLabel htmlFor="decision-dialog-input">{label}</FieldLabel>
        <Textarea
          id="decision-dialog-input"
          rows={3}
          maxLength={300}
          value={text}
          onChange={event => setText(event.target.value)}
        />
      </Field>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
          {t("COMMON.CANCEL")}
        </Button>
        <Button
          variant={destructive ? "destructive" : "default"}
          disabled={tooShort || isPending}
          loading={isPending}
          onClick={() => onConfirm(text.trim())}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
};
